import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Button, Grid } from '@mui/material';
import { getUserSelector } from '@store/auth';
import { getModalSelector } from '@store/modals';
import { ModalsService, PopupsService, TenantsService } from '@services';
import { MaskUtil } from '@utils';
import { ErrorsEnum, Modals, Popups } from '@enums';
import { Form, FormField, Modal, TextField } from '@elements';
import { getAddNewOrganizationSchema } from './add-new-organization.schema';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

/**
 * Add new user modal component.
 *
 * @author Ihar Kazlouski
 * @function AddNewUserModal
 * @category Modals
 * @return {FC} add new user modal component.
 */
var AddNewOrganizationModal = function AddNewOrganizationModal() {
  var _modalData$phone_numb;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var modal = useSelector(getModalSelector(Modals.AddNewOrganization));

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      error = _useState2[0],
      setError = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      phoneError = _useState4[0],
      setPhoneError = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isButtonBlocked = _useState6[0],
      setIsButtonBlocked = _useState6[1];

  var modalData = modal === null || modal === void 0 ? void 0 : modal.data;
  var formRef = useRef();
  var authUser = useSelector(getUserSelector);
  /**
   * Submit form.
   *
   * @author Ihar Kazlouski
   * @param {AddNewOrganizationValues} values values.
   * @return {Promise<void>}
   */

  var handleSuccess = function handleSuccess() {
    PopupsService.open(Popups.Success, {
      header: t('components.popups.success'),
      body: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewOrganization.organizationUpdated') : t('components.modals.addNewOrganization.organizationAdded'),
      acceptButton: t('components.popups.ok'),
      width: 270,
      maxWidth: 'xs'
    }).then(function () {
      ModalsService.close(Modals.AddNewOrganization);
      modalData === null || modalData === void 0 ? void 0 : modalData.updateAfterEdit();
    })["catch"](function () {
      ModalsService.close(Modals.AddNewOrganization);
      modalData === null || modalData === void 0 ? void 0 : modalData.updateAfterEdit();
    });
  };
  /**
   * Submit form.
   *
   * @author Ruslan Kolesov
   * @param {AddNewOrganizationValues} values values.
   * @return {Promise<void>}
   */


  var handleSubmit = function handleSubmit(values) {
    return function () {
      void _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _error, _error2;

        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!(modalData !== null && modalData !== void 0 && modalData.isEdit)) {
                  _context.next = 16;
                  break;
                }

                _context.prev = 1;
                setIsButtonBlocked(true);
                _context.next = 5;
                return TenantsService.apiEditTenantPut(authUser === null || authUser === void 0 ? void 0 : authUser.id, {
                  body: {
                    name: (values === null || values === void 0 ? void 0 : values.name) || '',
                    email: (values === null || values === void 0 ? void 0 : values.email) || '',
                    phone_number: values !== null && values !== void 0 && values.phone_number ? '+' + (values === null || values === void 0 ? void 0 : values.phone_number) : '',
                    contact_name: values === null || values === void 0 ? void 0 : values.contact_name,
                    status: modalData === null || modalData === void 0 ? void 0 : modalData.status,
                    id: modalData === null || modalData === void 0 ? void 0 : modalData.id
                  }
                });

              case 5:
                setIsButtonBlocked(false);
                handleSuccess();
                _context.next = 14;
                break;

              case 9:
                _context.prev = 9;
                _context.t0 = _context["catch"](1);
                _error = _context.t0;
                setIsButtonBlocked(false);

                if (_error.errorCode === ErrorsEnum.NameIsAlreadyExists) {
                  setError(t('components.modals.addNewOrganization.nameError'));
                } else if (_error.errorCode === ErrorsEnum.StartsWithZero) {
                  setPhoneError(t('components.modals.addNewUser.startWithZero'));
                } else {
                  void PopupsService.open(Popups.Error, {
                    header: t('components.popups.errorTitle'),
                    body: t('components.modals.addNewOrganization.editError'),
                    acceptButton: t('components.popups.ok'),
                    width: 270,
                    maxWidth: 'xs'
                  })["catch"](function () {// error handler
                  });
                }

              case 14:
                _context.next = 29;
                break;

              case 16:
                _context.prev = 16;
                setIsButtonBlocked(true);
                _context.next = 20;
                return TenantsService.apiCreateTenantPost((authUser === null || authUser === void 0 ? void 0 : authUser.id) || '', {
                  body: {
                    name: (values === null || values === void 0 ? void 0 : values.name) || '',
                    email: (values === null || values === void 0 ? void 0 : values.email) || '',
                    phone_number: values !== null && values !== void 0 && values.phone_number ? '+' + (values === null || values === void 0 ? void 0 : values.phone_number) : '',
                    contact_name: values === null || values === void 0 ? void 0 : values.contact_name
                  }
                });

              case 20:
                setIsButtonBlocked(false);
                handleSuccess();
                _context.next = 29;
                break;

              case 24:
                _context.prev = 24;
                _context.t1 = _context["catch"](16);
                _error2 = _context.t1;
                setIsButtonBlocked(false);

                if (_error2.errorCode === ErrorsEnum.NameIsAlreadyExists) {
                  setError(t('components.modals.addNewOrganization.nameError'));
                } else if (_error2.errorCode === ErrorsEnum.StartsWithZero) {
                  setPhoneError(t('components.modals.addNewUser.startWithZero'));
                } else {
                  void PopupsService.open(Popups.Error, {
                    header: t('components.popups.errorTitle'),
                    body: t('components.modals.addNewOrganization.createError'),
                    acceptButton: t('components.popups.ok'),
                    width: 270,
                    maxWidth: 'xs'
                  })["catch"](function () {// error handler
                  });
                }

              case 29:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, null, [[1, 9], [16, 24]]);
      }))();
    };
  };
  /**
   * Close modal.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var closeModal = function closeModal() {
    var _formRef$current, _formRef$current$getS;

    if (!(formRef !== null && formRef !== void 0 && (_formRef$current = formRef.current) !== null && _formRef$current !== void 0 && (_formRef$current$getS = _formRef$current.getState()) !== null && _formRef$current$getS !== void 0 && _formRef$current$getS.pristine)) {
      PopupsService.open(Popups.Default, {
        header: t('components.popups.unsavedChanges'),
        body: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewUser.quitEditModal') : t('components.modals.addNewUser.quitAddingModal'),
        acceptButton: t('components.popups.yes'),
        rejectButton: t('components.popups.cancel'),
        maxWidth: 'xs',
        width: 270
      }).then(function () {
        return ModalsService.close(Modals.AddNewOrganization);
      })["catch"](function () {// error handling
      });
    } else {
      ModalsService.close(Modals.AddNewOrganization);
    }
  };
  /**
   * Parse script tags.
   *
   * @author Ihar Kazlouski
   * @param {unknown} value value.
   * @return {void}
   */


  var handleScriptParse = function handleScriptParse(value) {
    var parseValue = value;
    var regex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
    return parseValue.replace(regex, '');
  };
  /**
   * Handle change function.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleChange = function handleChange() {
    if (error) {
      setError(null);
    }
  };

  return /*#__PURE__*/_jsxs(Modal, {
    fullWidth: true,
    maxWidth: "md",
    children: [/*#__PURE__*/_jsx(Modal.Title, {
      handleClose: closeModal,
      children: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewOrganization.editOrganization') : t('components.modals.addNewOrganization.addOrganization')
    }), /*#__PURE__*/_jsx(Modal.Content, {
      children: /*#__PURE__*/_jsx(Form, {
        onSubmit: handleSubmit,
        subscription: {
          pristine: true,
          submitting: true,
          invalid: true,
          values: true
        },
        initialValues: {
          name: (modalData === null || modalData === void 0 ? void 0 : modalData.name) || '',
          email: (modalData === null || modalData === void 0 ? void 0 : modalData.email) || '',
          phone_number: (modalData === null || modalData === void 0 ? void 0 : (_modalData$phone_numb = modalData.phone_number) === null || _modalData$phone_numb === void 0 ? void 0 : _modalData$phone_numb.substring(1)) || '',
          contact_name: modalData === null || modalData === void 0 ? void 0 : modalData.contact_name
        },
        validateSchema: getAddNewOrganizationSchema(error, phoneError),
        render: function render(_ref2) {
          var pristine = _ref2.pristine,
              submitting = _ref2.submitting,
              invalid = _ref2.invalid,
              values = _ref2.values,
              form = _ref2.form;
          formRef.current = form;
          return /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(Grid, {
              container: true,
              spacing: 2,
              children: [/*#__PURE__*/_jsxs(Grid, {
                container: true,
                item: true,
                spacing: 5,
                children: [/*#__PURE__*/_jsx(Grid, {
                  item: true,
                  xs: 6,
                  children: /*#__PURE__*/_jsx(FormField, {
                    name: "name",
                    size: "small",
                    component: TextField,
                    parse: handleScriptParse,
                    onChange: handleChange,
                    inputProps: {
                      maxLength: 100
                    },
                    fullWidth: true,
                    label: t('components.modals.addNewOrganization.nameLabel')
                  })
                }), /*#__PURE__*/_jsx(Grid, {
                  item: true,
                  xs: 6,
                  children: /*#__PURE__*/_jsx(FormField, {
                    name: "email",
                    size: "small",
                    component: TextField,
                    inputProps: {
                      maxLength: 100
                    },
                    fullWidth: true,
                    label: t('components.modals.addNewOrganization.emailLabel')
                  })
                })]
              }), /*#__PURE__*/_jsxs(Grid, {
                container: true,
                item: true,
                spacing: 5,
                children: [/*#__PURE__*/_jsx(Grid, {
                  item: true,
                  xs: 6,
                  children: /*#__PURE__*/_jsx(FormField, {
                    name: "phone_number",
                    size: "small",
                    component: TextField,
                    placeholder: "+1 912-123-1234",
                    maskProps: MaskUtil.phoneMask,
                    fullWidth: true,
                    label: t('components.modals.addNewOrganization.phoneLabel')
                  })
                }), /*#__PURE__*/_jsx(Grid, {
                  item: true,
                  xs: 6,
                  children: /*#__PURE__*/_jsx(FormField, {
                    name: "contact_name",
                    size: "small",
                    inputProps: {
                      maxLength: 100
                    },
                    parse: handleScriptParse,
                    component: TextField,
                    fullWidth: true,
                    label: t('components.modals.addNewOrganization.contactLabel')
                  })
                })]
              })]
            }), /*#__PURE__*/_jsx(Box, {
              pt: 6,
              width: "100%",
              children: /*#__PURE__*/_jsx(Button, {
                variant: "contained",
                disabled: pristine || submitting || invalid || isButtonBlocked,
                fullWidth: true,
                onClick: handleSubmit(values),
                children: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewOrganization.save') : t('components.modals.addNewOrganization.add')
              })
            })]
          });
        }
      })
    })]
  });
};

export { AddNewOrganizationModal };